import { useState } from 'preact/hooks'
import { Bell, ListChecks, Trash2 } from 'lucide-preact'
import { HouseholdCard } from '../components/HouseholdCard'
import { describeSchedule } from '../components/schedule-text'
import { ResyncButton, SignOutButton } from '../components/SyncBadge'
import { enablePush, forgetPushEndpoint } from '../lib/push'
import { live, useAppStore } from '../store/store'

/**
 * Réglages: the household's people, its own chores, the reminder hour, the
 * notification permission, and the two account buttons.
 *
 * *Le foyer* is a component of its own rather than a fourth block inlined here,
 * so that this file stays a page that arranges sections instead of becoming the
 * 275-line everything-component `docs/architecture-review.md` §4.1 was written
 * about.
 *
 * The permission request lives here and nowhere else. `Notification.requestPermission()`
 * on load is denied outright by Chrome's heuristics and permanently on iOS, so
 * it has to sit behind a tap — `CLAUDE.md` §5.
 *
 * This is also where `docs/web-push.md` task 5 lands: a test-notification
 * button, feature detection up front, and disabling actually calling
 * `unsubscribe()`.
 */
export function Settings() {
  const [notice, setNotice] = useState('')

  const settings = useAppStore((s) => s.settings)
  const saveSettings = useAppStore((s) => s.saveSettings)
  const tasks = useAppStore((s) => s.tasks)
  const removeTask = useAppStore((s) => s.removeTask)

  // Deleted chores are tombstones until they age out, and a tombstone is not a
  // chore anybody wants listed.
  const customTasks = live(tasks)

  async function activateNotifications() {
    try {
      setNotice('Activation…')
      await enablePush()
      saveSettings({ reminderEnabled: true })
      setNotice('Notifications activées ✅')
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Impossible d’activer les notifications.')
    }
  }

  function disableNotifications() {
    saveSettings({ reminderEnabled: false })
    // Otherwise the startup reconciliation reads the missing subscription as
    // damage and puts it back — turning reminders off would undo itself.
    forgetPushEndpoint()
    setNotice('Rappels désactivés.')
  }

  return (
    <section class="card settings-card">
      <HouseholdCard />

      <div class="section-heading">
        <h2>Mes tâches</h2>
        <ListChecks size={20} />
      </div>

      {customTasks.length === 0 ? (
        <p class="notice muted">
          Les tâches que tu ajoutes depuis <strong>Aujourd’hui</strong> apparaissent ici.
        </p>
      ) : (
        <div class="custom-task-list">
          {customTasks.map((task) => (
            <div class="custom-task-row">
              <span class="task-emoji">{task.emoji}</span>
              <span class="custom-task-text">
                <strong>{task.label}</strong>
                <small>{describeSchedule(task)}</small>
              </span>
              <button
                class="icon-button small danger"
                onClick={() => removeTask(task.id)}
                aria-label={`Supprimer ${task.label}`}
              >
                <Trash2 size={17} />
              </button>
            </div>
          ))}
        </div>
      )}

      {customTasks.length > 0 && (
        <p class="notice muted">
          Supprimer une tâche ne supprime pas les cases déjà cochées : elles restent dans
          l’historique.
        </p>
      )}

      <div class="section-heading">
        <h2>Rappels quotidiens</h2>
        <Bell size={20} />
      </div>

      <label class="setting-row">
        <span>
          <strong>Heure du rappel</strong>
          <small>Tu peux la changer quand tu veux.</small>
        </span>
        <input
          type="time"
          value={settings.reminderTime}
          onInput={(event) =>
            saveSettings({ reminderTime: (event.currentTarget as HTMLInputElement).value })
          }
        />
      </label>

      <button class="primary" onClick={activateNotifications}>
        <Bell size={18} />
        {settings.reminderEnabled ? 'Réactiver les notifications' : 'Activer les notifications'}
      </button>

      {settings.reminderEnabled && (
        <button class="secondary" onClick={disableNotifications}>
          Désactiver le rappel quotidien
        </button>
      )}

      {notice && <p class="notice">{notice}</p>}

      <div class="info-box">
        <strong>Le dimanche est déjà préparé :</strong>
        <p>poubelles WC, entretien du robot, pains, biscuits, linge et préparation du lundi.</p>
      </div>

      <ResyncButton />
      <SignOutButton />
    </section>
  )
}
